import React from 'react';
import { Link, To } from 'react-router-dom';
import { type Cell } from '@tanstack/react-table';
import classNames from 'classnames';

import { EHeaderCellType } from '../../types/table';

import styles from './BodyCell.module.scss';

// Elements that handle their own click, so the row link must not navigate on top of them
const INTERACTIVE_SELECTOR =
    'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="link"]';

export interface IBodyCellProps {
    cell: Cell<any, unknown>;
    children?: React.ReactNode;
    lastRow?: boolean;
    dense?: boolean;
    rowLink?: To;
}

export const BodyCell: React.FC<IBodyCellProps> = ({ cell, children, lastRow, dense, rowLink }) => {
    const columnMeta = cell.column.columnDef.meta;
    const headCellType = columnMeta?.headerCellType || EHeaderCellType.TEXT;
    // The cell padding moves onto the link, so that the whole cell area is clickable
    const withLink = rowLink !== undefined && !columnMeta?.disableRowLink;

    // Runs in the capture phase, i.e. before the nested element can stop the propagation.
    // Stopping the propagation would not be enough on its own: the row link is a real anchor,
    // so without preventDefault() the browser navigates even when no React handler sees the click.
    const handleClickCapture = (event: React.MouseEvent<HTMLTableCellElement>) => {
        if (!withLink) return;
        const interactive = (event.target as HTMLElement | null)?.closest(INTERACTIVE_SELECTOR);
        if (interactive && !interactive.classList.contains(styles.link)) {
            event.preventDefault();
        }
    };

    return (
        <td
            onClickCapture={handleClickCapture}
            onClick={(e) => {
                columnMeta?.onCellClick?.(cell, e);
            }}
            className={classNames(
                styles.td,
                styles[`type-${headCellType}`],
                {
                    [styles.lastRow]: !!lastRow,
                    [styles[`align-${columnMeta?.align}`]]: !!columnMeta?.align,
                    [styles.dense]: !!dense,
                    [styles.withLink]: withLink,
                },
                columnMeta?.cellClassName,
            )}
            style={{
                ...columnMeta?.cellStyle,
                width: cell.column.getSize(),
            }}
        >
            {withLink ? (
                <Link to={rowLink} className={classNames(styles.cellContent, styles.link)}>
                    {children}
                </Link>
            ) : (
                <div className={styles.cellContent}>{children}</div>
            )}
        </td>
    );
};
